import { ChartSeriesXY } from '../data/chart-data-types';
import { DashboardWidget } from './dashboard-widget';
import { DateRange } from './dashboard-filter';
import { html, PropertyValues } from 'lit';
import { moneyToCompactStr } from '../../../../webmodule-common/other/currency-formatter';
import { property } from 'lit/decorators.js';
import { serverDateTimeToLocalDateTime } from '../../../../webmodule-common/other/datetime-converter';
import { Snippet } from '../../../../webmodule-common/interop/webmodule-interop';
import { TimelineWidgetModal } from './timeline-widget-modal';

export const ApexCharts = (globalThis as any).ApexCharts;

export abstract class ApexChartWidget extends DashboardWidget {
  chart: any | null = null;

  @property({ type: Object }) dateRange?: DateRange;
  @property() branchId?: string;
  @property() userId?: string;

  private chartElementClass: string = '';

  constructor(chartElementClass: string) {
    super();

    this.chartElementClass = chartElementClass;
  }

  protected firstUpdated(): void {
    // Show a lightweight text placeholder while data loads. We deliberately do
    // NOT create a real ApexCharts placeholder here: its async render() can
    // resolve *after* updateData() has drawn the real per-currency charts and
    // re-insert the empty "Loading..." chart over the data (a race that surfaces
    // when several chart widgets load at once). updateData() clears this
    // placeholder before it renders.
    const container = this.querySelector(`.${this.chartElementClass}`);
    if (container && container.childElementCount === 0) {
      const placeholder = document.createElement('div');
      placeholder.className = 'dashboard-chart-placeholder';
      placeholder.textContent = 'Loading...';
      container.appendChild(placeholder);
    }
  }

  protected updated(changedProperties: PropertyValues) {
    super.updated(changedProperties);
    this.updateData();
  }

  protected abstract getTitle(): string;

  protected getHeader(): Snippet {
    return this.getTitle();
  }

  protected getBody(): Snippet {
    return html`<div class="${this.chartElementClass}"></div>`;
  }

  protected getYAxisLabel(value: number): string {
    return `${moneyToCompactStr(value)}`;
  }

  protected async onChartClick(_event: any, _chartContext: any, config: any, currency: string) {
    const series = config.config.series as ChartSeriesXY[];
    const modal = new TimelineWidgetModal();
    modal.eventTitle = this.getTitle();
    modal.seriesData = series;
    modal.currencySymbol = currency;
    modal.xAxisCategories = this.getCategoryLabels();
    await modal.showModal();
  }

  protected getCategoryLabels(): string[] {
    const labels: string[] = [];
    if (this.dateRange) {
      for (
        let currentDate = new Date(this.dateRange.startDate);
        currentDate <= this.dateRange.endDate;
        currentDate.setDate(currentDate.getDate() + 1)
      ) {
        labels.push(currentDate.toDateString());
      }
    }
    return labels;
  }

  protected convertDateToMillis(dateStr: string): number {
    let dt = serverDateTimeToLocalDateTime(dateStr);
    dt = dt.set({ hour: 0, minute: 0 });
    return dt.toMillis();
  }

  protected abstract updateData(): void;
}
